<template>
  <div class="widget w-mini-most-active-widget">
    <c-widget-header hideMenu hideLinkModeReceive :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <c-widget-control-bar-group inline>
        <v-form-group v-if="controlSecuritiesType === 'stocks'" label="">
          <v-form-select
            :options="optionsExchange"
            :value="controlExchange"
            @input="onExchangeChange"
            :aria-label="$t('white-label.widgets.mostActive.controls.labels.exchange')"
          />
        </v-form-group>
        <v-form-group v-else label="">
          <v-form-select
            :options="optionsCountries"
            :value="controlCountry"
            @input="onCountryChange"
            :aria-label="$t('white-label.widgets.mostActive.controls.labels.country')"
          />
        </v-form-group>
      </c-widget-control-bar-group>
      <c-widget-control-bar-group inline>
        <v-form-group>
          <c-widget-input-switch
            :options="securitiesTypeOptions"
            :value="controlSecuritiesType"
            @input="onSecuritiesTypeChange"
          />
        </v-form-group>
      </c-widget-control-bar-group>
    </c-widget-control-bar>

    <div class="widget-body">
      <ag-grid-vue
        v-if="!isError"
        animateRows
        suppressContextMenu
        class="c-data-grid ag-theme-balham-dark"
        ensureDomOrder
        :gridOptions="gridOptions"
        :defaultColDef="defaultColumnDef"
        :columnDefs="columnDefs"
        :context="this"
        immutableData
        :asyncTransactionWaitMillis="gridUpdateInterval"
        :rowHeight="agGridRowHeight"
        rowSelection="single"
        suppressFieldDotNotation
        suppressDragLeaveHidesColumns
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        @grid-ready="onGridReady"
        @row-selected="updateLink"
        @sort-changed="onTableSort"
        @cell-focused="onCellFocused"
        @async-transactions-flushed="onAsyncTransactionsFlushed"
        @column-resized="onColResized"
      />
      <v-alert show v-else-if="['nodata', 'nopermission'].includes(isError)" variant="light" align="center">{{
        i18n(`messages.${isError}`, 'widgets.mostActive')
      }}</v-alert>
      <v-alert show v-else variant="red" align="center">{{ i18n('messages.error', 'widgets.mostActive') }}</v-alert>
    </div>
  </div>
</template>

<script>
import agGridCommons from '@mixins/agGridCommons';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';

import { RowButtonRenderer } from '@custom/dataGrid/renderers';

import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';

export default {
  name: 'w-mini-most-active-widget',
  mixins: [agGridCommons],
  components: {
    CWidgetHeader,
    CWidgetHeaderButton,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CWidgetInputSwitch,
    AgGridVue,
    RowButtonRenderer
  },
  props: {
    isSidebar: Boolean,
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      isError: false,
      selectedMarket: '',
      subscriptionId: null,
      formats: {},
      gridOptions: {
        getRowNodeId(data) {
          return data['q.RIC'];
        },
        columnTypes: { actionable: {} }
      },
      optionsCountries: [
        {
          text: this.i18n('CA', 'countries'),
          value: 'CA'
        },
        {
          text: this.i18n('US', 'countries'),
          value: 'US'
        }
      ],
      optionsExchange: [
        // { value: 'GER', text: 'Xetra Level 1' }, // for testing purposes
        { value: 'TOR', text: 'TSX' },
        { value: 'CVE', text: 'TSX Venture' },
        { value: 'NYQ', text: 'NYSE' },
        { value: 'ASQ', text: 'AMEX' },
        { value: 'NSQ', text: 'NASDAQ' }
      ],
      optionsIntervalMap: {
        '5m': 'm5',
        '15m': 'm15',
        h: 'm60',
        d: 'day'
      },
      optionsResults: [5, 10, 20],
      pollingInterval: 300, //s
      pollingTimeout: 0,
      priceTrend: {},
      reconnectOnErrorTime: 30, //s
      resizeTimeout: 0,
      tickerColWidthOptions: 135,
      tableFieldsPrepend: ['action']
    };
  },
  computed: {
    extendedColDefs() {
      return {
        action: {
          cellRenderer: 'RowButtonRenderer',
          width: 25,
          resizable: false
        },
        'q.RIC': {
          hide: true
        },
        'q._COUNTRY': {
          hide: true
        },
        'q._TCR': {
          width: 60
        },
        'q._TRDPRC_1': {
          width: 80
        },
        'q._PCTCHNG': {
          width: 64
        }
      };
    },
    controlCountry() {
      return this.$store.state.widget[this.wid].country;
    },
    controlCriteria() {
      return this.$store.state.widget[this.wid].criteria;
    },
    controlExchange() {
      return this.$store.state.widget[this.wid].exchange;
    },
    controlInterval() {
      return this.$store.state.widget[this.wid].interval;
    },
    controlResults() {
      return this.$store.state.widget[this.wid].results;
    }
  },
  methods: {
    onCountryChange(countryCode) {
      this.$store.commit(`widget/${this.wid}/COUNTRY_CHANGED`, countryCode);
      this.fetch();
    },
    onExchangeChange(ev) {
      this.$store.commit(`widget/${this.wid}/EXCHANGE_CHANGED`, ev);
      this.fetch();
    },
    getScreenerData() {
      const url =
        this.controlSecuritiesType === 'options'
          ? `/find/optionscreener/mostactive?country=${this.controlCountry}&interval=${
              this.optionsIntervalMap[this.controlInterval]
            }&limit=${this.controlResults}`
          : `/stockscreener/mostactive?exchanges=${this.controlExchange}&fids=q.RIC,${this.controlCriteria}&interval=${
              this.controlInterval
            }&count=${this.controlResults}&sortBy=${this.controlCriteria.replace('stoscr.', '')}`;
      return API.Rest.get(url)
        .then(res => {
          // console.warn(res);
          if (!res.data.data.length) {
            return Promise.reject('nodata');
          }
          Object.assign(this.formats, {
            ['stoscr.NUM_MOVES(0)']: 'int',
            ['stoscr.VOLUME(0)']: 'int'
          }); // this should be replaced with actual metadata from response, which now is empty
          return this.controlSecuritiesType === 'options'
            ? res.data.data.map(option => ({
                'q.RIC': option.ric,
                'stoscr.VOLUME(0)': option.volume
              }))
            : res.data.data;
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            throw 'nopermission';
          }
          throw err;
        });
    },
    getSnapshot(screenerData) {
      const rics = screenerData.map(row => row['q.RIC']);
      return API.Rest.get(`/quote/info?rics=${rics}&fids=${this.requestFids}`).then(response => {
        Object.assign(
          this.formats,
          Object.keys(response.data.metadata).reduce((acc, ric) => {
            acc[ric] = response.data.metadata[ric].type;
            return acc;
          }, {})
        );
        this.isError = false;
        const rowsData = [];
        response.data.data.forEach(data => {
          const row = this.gridOptions.api.getRowNode(data['q.RIC']);
          rowsData.push(
            this.hydrateData(
              { ...data, ...screenerData.find(stoscrRow => stoscrRow['q.RIC'] === data['q.RIC']) },
              row ? row.data : {}
            )
          );
        });
        this.gridOptions.api.setRowData(rowsData);
        this.isLoading = false;
        const timeout = this.getPollingTimeout();
        this.pollingTimeout = setTimeout(this.fetch, timeout, true);
        return rics;
      });
    },
    getPollingTimeout() {
      const step = 30000;
      let scheduledTime = Date.now() + this.pollingInterval * 1000;
      // schedule next stockscreener data update with provided interval, taking into account minutes of the hour. Do upate only on 4 and 9 minutes in order to display most useful information regarding the interval setting
      while (new Date(scheduledTime).getMinutes() % 5 != 4) {
        scheduledTime -= step;
      }
      // console.warn(scheduledTime - Date.now());
      return scheduledTime - Date.now();
    },
    handleUpdate(response, error) {
      if (error) {
        console.error('Streaming update error', error);
        return;
      }
      // most probably unsubscribe didn't work (so far), try it again
      if (this.isError) {
        this.streamingUnsubscribe();
        return;
      }
      response = response.payload[0];
      const rowNode = this.gridOptions.api.getRowNode(response.ric);
      const data = this.hydrateData(response.data, rowNode.data);
      if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.applyTransactionAsync({ update: [data] });
    },
    fetch(silent) {
      if (document.visibilityState !== 'visible') return;
      if (!silent) this.isLoading = true;
      clearTimeout(this.pollingTimeout);
      this.isError = false;
      this.streamingUnsubscribe();
      if (this.gridOptions.api && !silent) this.gridOptions.api.setRowData([]);
      this.getScreenerData()
        .then(this.getSnapshot)
        .then(this.streamingSubscribe)
        .catch(e => {
          if (e === 'nodata') {
            this.isError = 'nodata';
          } else if (e === 'nopermission') {
            this.isError = 'nopermission';
          } else {
            console.error(e);
            this.isError = true;
            this.pollingTimeout = setTimeout(() => {
              this.fetch();
            }, this.reconnectOnErrorTime * 1000);
          }
          this.isLoading = false;
        });
    },
    onGridReady(ev) {
      this.onTableReady(ev, this.$t('white-label.widgets.miniMostActive.widget-title'));
    }
  },
  // Life-cycle
  mounted() {
    this.fetch();
  },
  beforeDestroy() {
    clearTimeout(this.pollingTimeout);
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>
<style lang="scss">
@import '~@/styles/widgets/mini-most-active-widget';
</style>
